/**
 * Call It In LLC — Employee Management
 *
 * Full staff-management surface for dispatch/admin.
 *
 * Supports:
 *   - View with status/role/search filters
 *   - Add employee (full profile)
 *   - Edit employee (identity, role, phone, vehicle type, certifications, optional password reset)
 *   - Deactivate (soft) / Reactivate
 *
 * Hard delete is intentionally blocked: user UUIDs are foreign-keyed by
 * assigned_driver_id on tasks and by audit_events. Deactivation is the
 * correct permanent-removal path and is fully reversible.
 *
 * Compliance fields (phone, vehicle_type, certifications) are now persisted
 * in the backend users table and returned by GET /auth/users.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";

import DashboardLayout from "../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../examples/Navbars/DashboardNavbar";
import MDBox from "../components/MDBox";
import MDTypography from "../components/MDTypography";
import MDButton from "../components/MDButton";
import MDInput from "../components/MDInput";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ComplexStatisticsCard from "../examples/Cards/StatisticsCards/ComplexStatisticsCard";
import {
  listUsers,
  createUser,
  updateUser,
  deactivateUser,
  reactivateUser,
  getTasks,
} from "../api/client";

// ── Constants ────────────────────────────────────────────────────────────────

const ROLES = ["driver", "admin", "dispatcher"];

const VEHICLE_TYPES = [
  "N/A", "Sedan", "SUV", "Van", "Sprinter", "Box Truck", "Refrigerated", "Ambient",
];

const CERT_OPTIONS = [
  { id: "NEMT",      label: "NEMT",       desc: "Non-Emergency Medical Transport accredited" },
  { id: "HazMat",   label: "HazMat",     desc: "Hazardous Materials authorized" },
  { id: "TWIC",     label: "TWIC",       desc: "Transportation Worker Identification Credential" },
  { id: "CDL",      label: "CDL",        desc: "Commercial Driver's License" },
  { id: "First Aid",label: "First Aid",  desc: "First Aid / CPR certified" },
  { id: "HIPAA",    label: "HIPAA",      desc: "HIPAA compliance training completed" },
];

const roleColor = (role) => {
  if (role === "admin")      return { bg: "#ede9fe", text: "#7c3aed", border: "#c4b5fd" };
  if (role === "driver")     return { bg: "#e0f2fe", text: "#0369a1", border: "#7dd3fc" };
  if (role === "dispatcher") return { bg: "#dcfce7", text: "#15803d", border: "#86efac" };
  return { bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1" };
};

// ── hydrateUser ──────────────────────────────────────────────────────────────

function hydrateUser(u) {
  return {
    id:            u.id,
    name:          u.name  || u.email,
    email:         u.email,
    role:          u.role,
    is_active:     u.is_active ?? 1,
    created_at:    u.created_at,
    phone:         u.phone        || "",
    vehicle_type:  u.vehicle_type || "N/A",
    certifications: Array.isArray(u.certifications) ? u.certifications : [],
  };
}

// ── Cert chip toggle ─────────────────────────────────────────────────────────

function CertSelector({ value = [], onChange }) {
  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((c) => c !== id) : [...value, id]);
  };
  return (
    <MDBox>
      <Typography variant="caption" sx={{ fontWeight: 600, color: "#475569", letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 0.75 }}>
        Certifications &amp; Credentials
      </Typography>
      <MDBox display="flex" flexWrap="wrap" gap={0.75}>
        {CERT_OPTIONS.map(({ id, label, desc }) => {
          const active = value.includes(id);
          return (
            <Tooltip key={id} title={desc} arrow placement="top">
              <Chip
                label={label}
                size="small"
                onClick={() => toggle(id)}
                variant={active ? "filled" : "outlined"}
                sx={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  letterSpacing: "0.04em",
                  ...(active
                    ? { bgcolor: "#1e293b", color: "#f1f5f9", borderColor: "#1e293b" }
                    : { borderColor: "#cbd5e1", color: "#64748b" }),
                  transition: "all 0.15s",
                }}
              />
            </Tooltip>
          );
        })}
      </MDBox>
    </MDBox>
  );
}

// ── Section divider label ────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography variant="caption" sx={{
      display: "block", fontWeight: 700, letterSpacing: "0.07em",
      textTransform: "uppercase", color: "#94a3b8", mt: 1, mb: 0.25,
    }}>
      {children}
    </Typography>
  );
}

// ── Employee profile form fields (shared by Add + Edit) ──────────────────────

function ProfileFields({ form, onChange, showPassword, isEdit = false }) {
  const [revealPwd, setRevealPwd] = useState(false);

  return (
    <>
      <SectionLabel>Identity</SectionLabel>
      <MDBox display="flex" gap={2}>
        <MDInput
          label="Full Name"
          value={form.name}
          onChange={(e) => onChange("name", e.target.value)}
          fullWidth required autoFocus={!isEdit}
          disabled={form._saving}
        />
        <MDInput
          type="email"
          label="Email Address"
          value={form.email}
          onChange={(e) => onChange("email", e.target.value)}
          fullWidth required
          disabled={form._saving}
        />
      </MDBox>

      <MDBox display="flex" gap={2} alignItems="flex-start">
        <FormControl fullWidth size="small">
          <InputLabel id="form-role-label">Role</InputLabel>
          <Select
            labelId="form-role-label"
            value={form.role}
            label="Role"
            onChange={(e) => onChange("role", e.target.value)}
            disabled={form._saving}
            sx={{ height: 44 }}
          >
            {ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <MDInput
          label="Phone"
          value={form.phone}
          onChange={(e) => onChange("phone", e.target.value)}
          fullWidth
          placeholder="+1 (720) 000-0000"
          disabled={form._saving}
        />
      </MDBox>

      <SectionLabel>Operational Profile</SectionLabel>
      <FormControl fullWidth size="small">
        <InputLabel id="form-vehicle-label">Vehicle / Capability Type</InputLabel>
        <Select
          labelId="form-vehicle-label"
          value={form.vehicle_type}
          label="Vehicle / Capability Type"
          onChange={(e) => onChange("vehicle_type", e.target.value)}
          disabled={form._saving}
          sx={{ height: 44 }}
        >
          {VEHICLE_TYPES.map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <CertSelector
        value={form.certifications}
        onChange={(certs) => onChange("certifications", certs)}
      />

      {showPassword && (
        <>
          <SectionLabel>{isEdit ? "Password Reset (optional)" : "Access"}</SectionLabel>
          <MDInput
            type={revealPwd ? "text" : "password"}
            label={isEdit ? "New Password — leave blank to keep current" : "Password"}
            value={form.password}
            onChange={(e) => onChange("password", e.target.value)}
            fullWidth
            required={!isEdit}
            disabled={form._saving}
            helperText={
              isEdit
                ? (form.password ? "Minimum 6 characters" : "Leave blank to keep existing password")
                : "Minimum 6 characters"
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setRevealPwd((v) => !v)} size="small" edge="end">
                    <Icon sx={{ fontSize: "1.1rem !important" }}>
                      {revealPwd ? "visibility_off" : "visibility"}
                    </Icon>
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </>
      )}
    </>
  );
}

// ── Add Employee modal ────────────────────────────────────────────────────────

const EMPTY_ADD = {
  name: "", email: "", password: "", role: "driver",
  phone: "", vehicle_type: "N/A", certifications: [], _saving: false,
};

function AddEmployeeModal({ open, onClose, onAdded }) {
  const [form, setForm]   = useState(EMPTY_ADD);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setForm(EMPTY_ADD); setError(""); }
  }, [open]);

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim())     return setError("Full name is required.");
    if (!form.email.trim())    return setError("Email is required.");
    if (!form.password.trim()) return setError("Password is required.");
    if (form.password.length < 6) return setError("Password must be at least 6 characters.");

    setForm((p) => ({ ...p, _saving: true }));
    try {
      const res  = await createUser({
        name: form.name.trim(), email: form.email.trim(),
        password: form.password, role: form.role,
        phone: form.phone.trim() || null,
        vehicle_type: form.vehicle_type !== "N/A" ? form.vehicle_type : null,
        certifications: form.certifications,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onAdded(data);
      } else if (res.status === 409) {
        setError("A user with that email already exists.");
      } else {
        setError(data?.detail ?? `Failed to create employee (HTTP ${res.status}).`);
      }
    } catch {
      setError("Cannot reach the server. Check your connection and try again.");
    } finally {
      setForm((p) => ({ ...p, _saving: false }));
    }
  };

  return (
    <Dialog open={open} onClose={!form._saving ? onClose : undefined}
      maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Add New Employee</DialogTitle>
      <Divider />
      <DialogContent>
        <MDBox component="form" onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
          <ProfileFields form={form} onChange={set} showPassword isEdit={false} />
          <MDBox display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={form._saving}>
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="success"
              disabled={form._saving} sx={{ minWidth: 140 }}>
              {form._saving ? <CircularProgress size={18} color="inherit" /> : "Add Employee"}
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Employee modal ───────────────────────────────────────────────────────

function EditEmployeeModal({ employee, open, onClose, onSaved }) {
  const [form, setForm]   = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (employee) {
      setForm({
        name: employee.name, email: employee.email, role: employee.role,
        phone: employee.phone || "",
        vehicle_type: employee.vehicle_type || "N/A",
        certifications: employee.certifications || [],
        password: "",
        _saving: false,
      });
      setError("");
    }
  }, [employee]);

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim())  return setError("Full name is required.");
    if (!form.email.trim()) return setError("Email is required.");
    if (form.password && form.password.length < 6)
      return setError("New password must be at least 6 characters.");

    setForm((p) => ({ ...p, _saving: true }));
    try {
      const payload = {
        name: form.name.trim(), email: form.email.trim(), role: form.role,
        phone: form.phone.trim() || null,
        vehicle_type: form.vehicle_type !== "N/A" ? form.vehicle_type : null,
        certifications: form.certifications,
      };
      if (form.password.trim()) payload.password = form.password.trim();

      const res  = await updateUser(employee.id, payload);
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        onSaved({ ...employee, ...data });
      } else if (res.status === 409) {
        setError("That email address is already in use by another account.");
      } else {
        setError(data?.detail ?? `Save failed (HTTP ${res.status}).`);
      }
    } catch {
      setError("Cannot reach the server. Check your connection and try again.");
    } finally {
      setForm((p) => ({ ...p, _saving: false }));
    }
  };

  return (
    <Dialog open={open} onClose={!form._saving ? onClose : undefined}
      maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        Edit Employee
        {employee && (
          <Typography variant="caption" sx={{ display: "block", color: "#94a3b8", fontWeight: 400, mt: 0.25 }}>
            {employee.email}
          </Typography>
        )}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <MDBox component="form" onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
          <ProfileFields form={form} onChange={set} showPassword isEdit />
          <MDBox display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={form._saving}>
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info"
              disabled={form._saving} sx={{ minWidth: 140 }}>
              {form._saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

// ── Confirm dialog ─────────────────────────────────────────────────────────

function ConfirmDialog({ open, title, body, confirmLabel, confirmColor = "error", onConfirm, onClose, loading }) {
  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined}
      maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <MDBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>{body}</MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color={confirmColor} onClick={onConfirm}
          disabled={loading} sx={{ minWidth: 120 }}>
          {loading ? <CircularProgress size={16} color="inherit" /> : confirmLabel}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ isActive, onTask }) {
  return (
    <MDBox display="flex" flexDirection="column" gap={0.4}>
      <span style={{
        display: "inline-block", padding: "2px 9px", borderRadius: 10,
        fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase",
        background: isActive ? "#dcfce7" : "#f1f5f9",
        color:      isActive ? "#16a34a" : "#94a3b8",
      }}>
        {isActive ? "Active" : "Inactive"}
      </span>
      {isActive && onTask && (
        <span style={{
          fontSize: "0.65rem", fontWeight: 700, color: "#f59e0b",
          textTransform: "uppercase", letterSpacing: "0.04em",
        }}>
          On Task
        </span>
      )}
    </MDBox>
  );
}

// ── Role badge ────────────────────────────────────────────────────────────────

function RoleBadge({ role }) {
  const c = roleColor(role);
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 12,
      fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
    }}>
      {role}
    </span>
  );
}

// ── Cert badges ───────────────────────────────────────────────────────────────

function CertBadges({ certs }) {
  if (!certs || certs.length === 0)
    return <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>—</span>;
  return (
    <MDBox display="flex" flexWrap="wrap" gap={0.5}>
      {certs.map((c) => (
        <Tooltip key={c} title={CERT_OPTIONS.find((o) => o.id === c)?.desc || c} arrow>
          <span style={{
            display: "inline-block", padding: "1px 7px", borderRadius: 8,
            fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.04em",
            background: "#1e293b", color: "#f8fafc",
          }}>
            {c}
          </span>
        </Tooltip>
      ))}
    </MDBox>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Employees() {
  const [allEmployees, setAll]    = useState([]);
  const [loadingList, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [activeTasks, setTasks]   = useState([]);

  // Filters
  const [statusFilter, setStatus] = useState("active");
  const [roleFilter,   setRole]   = useState("all");
  const [search,       setSearch] = useState("");

  // Modals
  const [addOpen, setAddOpen]       = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmDialog, setConfirm] = useState({
    open: false, type: null, employee: null, loading: false, error: "", warning: null,
  });

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const res  = await listUsers();
      const data = await res.json().catch(() => null);
      if (res.ok && Array.isArray(data)) {
        // Vendor reps are external portal users, not staff — keep them strictly
        // separated and manageable only from Portal Access.
        setAll(data.filter((u) => u.role !== "vendor_rep").map(hydrateUser));
      } else {
        setListError(data?.detail ?? `Failed to load staff list (HTTP ${res.status}).`);
      }
    } catch {
      setListError("Cannot reach the server.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const res  = await getTasks();
      const data = await res.json().catch(() => []);
      if (res.ok && Array.isArray(data))
        setTasks(data.filter((t) => t.status !== "Delivered" && t.status !== "Complete"));
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchEmployees(); fetchTasks(); }, [fetchEmployees, fetchTasks]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const active   = allEmployees.filter((e) => e.is_active === 1);
    const inactive = allEmployees.filter((e) => e.is_active === 0);
    return {
      active:   active.length,
      inactive: inactive.length,
      drivers:  active.filter((e) => e.role === "driver").length,
      nemt:     active.filter((e) => e.certifications.includes("NEMT")).length,
    };
  }, [allEmployees]);

  // ── Filtered list ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...allEmployees];
    if (statusFilter === "active")   list = list.filter((e) => e.is_active === 1);
    if (statusFilter === "inactive") list = list.filter((e) => e.is_active === 0);
    if (roleFilter !== "all")        list = list.filter((e) => e.role === roleFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.email.toLowerCase().includes(q) ||
          e.role.toLowerCase().includes(q) ||
          (e.phone || "").includes(q),
      );
    }
    return list;
  }, [allEmployees, statusFilter, roleFilter, search]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const openDeactivate = (emp) => {
    const onTask = activeTasks.some(
      (t) => t.assigned_driver_id === emp.id || t.driver === emp.name,
    );
    setConfirm({
      open: true, type: "deactivate", employee: emp, loading: false, error: "",
      warning: onTask
        ? `${emp.name} currently has active tasks assigned. Deactivating will not unassign those tasks — review task assignments after proceeding.`
        : null,
    });
  };

  const openReactivate = (emp) =>
    setConfirm({ open: true, type: "reactivate", employee: emp, loading: false, error: "", warning: null });

  const handleConfirmAction = async () => {
    const { type, employee } = confirmDialog;
    setConfirm((p) => ({ ...p, loading: true, error: "" }));
    try {
      const res  = type === "deactivate" ? await deactivateUser(employee.id) : await reactivateUser(employee.id);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setConfirm((p) => ({ ...p, open: false, loading: false }));
        setAll((prev) =>
          prev.map((e) =>
            e.id === employee.id ? { ...e, is_active: type === "deactivate" ? 0 : 1 } : e,
          ),
        );
      } else {
        setConfirm((p) => ({
          ...p, loading: false,
          error: data?.detail ?? `Action failed (HTTP ${res.status}).`,
        }));
      }
    } catch {
      setConfirm((p) => ({ ...p, loading: false, error: "Cannot reach the server." }));
    }
  };

  const handleEditSaved = (updated) => {
    setEditTarget(null);
    setAll((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));
  };

  const handleAdded = () => { setAddOpen(false); fetchEmployees(); };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* Stats */}
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <ComplexStatisticsCard color="dark"    icon="verified_user"        title="Active Staff"     count={stats.active} />
            </Grid>
            <Grid item xs={6} md={3}>
              <ComplexStatisticsCard color="secondary" icon="person_off"         title="Inactive"         count={stats.inactive} />
            </Grid>
            <Grid item xs={6} md={3}>
              <ComplexStatisticsCard color="info"    icon="local_shipping"       title="Active Drivers"   count={stats.drivers} />
            </Grid>
            <Grid item xs={6} md={3}>
              <ComplexStatisticsCard color="success" icon="medical_services"     title="NEMT Accredited"  count={stats.nemt} />
            </Grid>
          </Grid>
        </MDBox>

        <Card sx={{ borderRadius: "1.25rem" }}>

          {/* Header */}
          <MDBox pt={3} px={3} pb={0} display="flex" justifyContent="space-between"
            alignItems="center" flexWrap="wrap" gap={1}>
            <MDTypography variant="h6" fontWeight="medium">
              Staff Directory &amp; Management
            </MDTypography>
            <MDButton
              variant="gradient" color="success" size="small"
              onClick={() => setAddOpen(true)}
              startIcon={<Icon>person_add</Icon>}
            >
              Add Employee
            </MDButton>
          </MDBox>

          {/* Filters */}
          <MDBox px={3} pt={2} pb={1.5} display="flex" flexWrap="wrap" gap={1.5} alignItems="center">

            {/* Status */}
            <MDBox display="flex" gap={0.75}>
              {[
                { key: "active",   label: `Active (${stats.active})`,   color: "success" },
                { key: "inactive", label: `Inactive (${stats.inactive})`, color: "default" },
                { key: "all",      label: "All",                          color: "primary" },
              ].map(({ key, label, color }) => (
                <Chip
                  key={key} size="small" label={label}
                  variant={statusFilter === key ? "filled" : "outlined"}
                  color={color}
                  onClick={() => setStatus(key)}
                  sx={{ cursor: "pointer", fontWeight: 600 }}
                />
              ))}
            </MDBox>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            {/* Role */}
            <MDBox display="flex" gap={0.75}>
              {["all", ...ROLES].map((r) => {
                const isActive = roleFilter === r;
                const c = r !== "all" ? roleColor(r) : null;
                return (
                  <Chip
                    key={r} size="small"
                    label={r === "all" ? "All Roles" : r.charAt(0).toUpperCase() + r.slice(1)}
                    variant={isActive ? "filled" : "outlined"}
                    onClick={() => setRole(r)}
                    sx={{
                      cursor: "pointer", fontWeight: 600,
                      ...(isActive && c
                        ? { bgcolor: c.text, color: "#fff", borderColor: c.text }
                        : {}),
                    }}
                  />
                );
              })}
            </MDBox>

            {/* Search */}
            <MDBox ml="auto">
              <MDInput
                placeholder="Search name, email, role, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ width: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ fontSize: "1rem !important", color: "text.secondary" }}>search</Icon>
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}>
                        <Icon sx={{ fontSize: "0.9rem !important" }}>close</Icon>
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </MDBox>
          </MDBox>

          {listError && (
            <MDBox px={3} pb={1}>
              <Alert severity="warning" onClose={() => setListError("")}>{listError}</Alert>
            </MDBox>
          )}

          {/* Table */}
          <MDBox sx={{ overflowX: "auto" }}>
            {loadingList ? (
              <MDBox display="flex" justifyContent="center" py={6}>
                <CircularProgress size={28} />
              </MDBox>
            ) : filtered.length === 0 ? (
              <MDBox py={5} textAlign="center">
                <MDTypography variant="body2" color="secondary">
                  {search || statusFilter !== "all" || roleFilter !== "all"
                    ? "No employees match the current filters."
                    : "No employees found."}
                </MDTypography>
              </MDBox>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    {["Staff Member", "Role", "Status", "Phone", "Vehicle", "Certifications", "Actions"].map((h) => (
                      <th key={h} style={{
                        padding: "10px 16px", textAlign: "left",
                        fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.07em",
                        textTransform: "uppercase", color: "#64748b", whiteSpace: "nowrap",
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((emp) => {
                    const isActive = emp.is_active === 1;
                    const onTask   = activeTasks.some(
                      (t) => t.assigned_driver_id === emp.id || t.driver === emp.name,
                    );
                    return (
                      <tr key={emp.id} style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: isActive ? "transparent" : "#fafafa",
                        opacity: isActive ? 1 : 0.72,
                        transition: "background 0.1s",
                      }}>
                        {/* Staff Member */}
                        <td style={{ padding: "13px 16px", minWidth: 180 }}>
                          <div style={{ fontWeight: 600, fontSize: "0.85rem", color: "#1e293b" }}>
                            {emp.name}
                          </div>
                          <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 2 }}>
                            {emp.email}
                          </div>
                        </td>

                        {/* Role */}
                        <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                          <RoleBadge role={emp.role} />
                        </td>

                        {/* Status */}
                        <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                          <StatusBadge isActive={isActive} onTask={onTask} />
                        </td>

                        {/* Phone */}
                        <td style={{ padding: "13px 16px", minWidth: 130 }}>
                          {emp.phone
                            ? <span style={{ fontSize: "0.78rem", color: "#475569" }}>{emp.phone}</span>
                            : <span style={{ fontSize: "0.75rem", color: "#cbd5e1" }}>—</span>
                          }
                        </td>

                        {/* Vehicle */}
                        <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                          {emp.vehicle_type && emp.vehicle_type !== "N/A" ? (
                            <span style={{
                              display: "inline-block", padding: "2px 8px", borderRadius: 8,
                              fontSize: "0.68rem", fontWeight: 600,
                              background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd",
                            }}>
                              {emp.vehicle_type}
                            </span>
                          ) : (
                            <span style={{ fontSize: "0.75rem", color: "#cbd5e1" }}>—</span>
                          )}
                        </td>

                        {/* Certifications */}
                        <td style={{ padding: "13px 16px", minWidth: 160 }}>
                          <CertBadges certs={emp.certifications} />
                        </td>

                        {/* Actions */}
                        <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                          <MDBox display="flex" gap={0.75} alignItems="center">
                            <Tooltip title="Edit profile and credentials" arrow>
                              <MDButton
                                variant="outlined" color="info" size="small"
                                onClick={() => setEditTarget(emp)}
                                sx={{ minWidth: 0, px: 1.2, py: 0.5, fontSize: "0.72rem" }}
                              >
                                <Icon sx={{ fontSize: "0.95rem !important", mr: 0.4 }}>edit</Icon>
                                Edit
                              </MDButton>
                            </Tooltip>

                            {isActive ? (
                              <Tooltip title="Block account from logging in" arrow>
                                <MDButton
                                  variant="outlined" color="warning" size="small"
                                  onClick={() => openDeactivate(emp)}
                                  sx={{ minWidth: 0, px: 1.2, py: 0.5, fontSize: "0.72rem" }}
                                >
                                  <Icon sx={{ fontSize: "0.95rem !important", mr: 0.4 }}>person_off</Icon>
                                  Deactivate
                                </MDButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Restore login access" arrow>
                                <MDButton
                                  variant="outlined" color="success" size="small"
                                  onClick={() => openReactivate(emp)}
                                  sx={{ minWidth: 0, px: 1.2, py: 0.5, fontSize: "0.72rem" }}
                                >
                                  <Icon sx={{ fontSize: "0.95rem !important", mr: 0.4 }}>person_add</Icon>
                                  Reactivate
                                </MDButton>
                              </Tooltip>
                            )}
                          </MDBox>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </MDBox>

          {/* Footer count */}
          {!loadingList && filtered.length > 0 && (
            <MDBox px={3} py={1.5} borderTop="1px solid #f1f5f9">
              <MDTypography variant="caption" color="secondary">
                Showing {filtered.length} of {allEmployees.length} employees
              </MDTypography>
            </MDBox>
          )}
        </Card>
      </MDBox>

      <AddEmployeeModal open={addOpen} onClose={() => setAddOpen(false)} onAdded={handleAdded} />

      <EditEmployeeModal
        employee={editTarget}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={handleEditSaved}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={
          confirmDialog.type === "deactivate"
            ? `Deactivate ${confirmDialog.employee?.name ?? "Employee"}?`
            : `Reactivate ${confirmDialog.employee?.name ?? "Employee"}?`
        }
        body={
          <>
            {confirmDialog.warning && (
              <Alert severity="warning" sx={{ fontSize: "0.78rem" }}>{confirmDialog.warning}</Alert>
            )}
            {confirmDialog.error && (
              <Alert severity="error" sx={{ fontSize: "0.78rem" }}>{confirmDialog.error}</Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {confirmDialog.type === "deactivate"
                ? "This blocks the account from logging in. Task history and audit records are preserved. This action is reversible."
                : "This restores full login access for this account."}
            </Typography>
          </>
        }
        confirmLabel={confirmDialog.type === "deactivate" ? "Deactivate" : "Reactivate"}
        confirmColor={confirmDialog.type === "deactivate" ? "warning" : "success"}
        onConfirm={handleConfirmAction}
        onClose={() => setConfirm((p) => ({ ...p, open: false }))}
        loading={confirmDialog.loading}
      />
    </DashboardLayout>
  );
}
